import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isLight = scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isLight
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group"
          >
            <span
              className={`font-heading text-xl md:text-2xl font-semibold tracking-tight transition-colors duration-300 ${
                isLight ? "text-foreground-900" : "text-background-50"
              }`}
            >
              BaseTao<span className="text-primary-400">Sheet</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5">
            {/* Home */}
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isLight
                  ? "text-foreground-700 hover:text-foreground-900 hover:bg-background-100"
                  : "text-background-50/90 hover:text-background-50 hover:bg-white/10"
              }`}
            >
              <i className="ri-home-4-line mr-1.5 text-sm"></i>
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isLight
                  ? "text-foreground-700 hover:text-foreground-900 hover:bg-background-100"
                  : "text-background-50/90 hover:text-background-50 hover:bg-white/10"
              }`}
            >
              <i className="ri-book-open-line mr-1.5 text-sm"></i>
              Shopping Guide
            </Link>

            {/* Category Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isLight
                    ? "text-foreground-700 hover:text-foreground-900 hover:bg-background-100"
                    : "text-background-50/90 hover:text-background-50 hover:bg-white/10"
                }`}
              >
                <i className="ri-grid-line text-base"></i>
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-background-50 border border-background-200/70 rounded-xl py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base text-primary-500`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-medium border rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                isLight
                  ? "text-foreground-700 hover:text-foreground-900 border-background-300 hover:border-background-400"
                  : "text-background-50 hover:text-background-50 border-background-50/40 hover:border-background-50/70 bg-white/5 hover:bg-white/10"
              }`}
            >
              BaseTao Spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-lg hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              isLight
                ? "text-foreground-700 hover:bg-background-100"
                : "text-background-50 hover:bg-white/10"
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-4 space-y-1 max-h-[80vh] overflow-y-auto">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-4-line text-base text-primary-500"></i>
              </span>
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-base text-primary-500"></i>
              </span>
              Shopping Guide
            </Link>

            <div className="mx-4 my-2 border-t border-background-200/50"></div>

            <p className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
              Product Categories
            </p>
            <div className="grid grid-cols-2 gap-1 px-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-xs text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm text-primary-500`}></i>
                  </span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>

            <div className="mx-4 my-2 border-t border-background-200/50"></div>

            <div className="px-4 flex flex-col gap-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-2.5 text-sm font-medium text-center text-foreground-700 border border-background-300 rounded-lg hover:border-background-400 transition-all cursor-pointer whitespace-nowrap"
              >
                BaseTao Spreadsheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-2.5 text-sm font-semibold text-center text-background-50 bg-primary-500 rounded-lg hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}